import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Avatar,
  Tabs,
  Tab,
  useTheme,
  TextField,
  Alert,
  Slider,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { 
  DragIndicator, 
  Visibility, 
  VisibilityOff, 
  EmojiEvents, 
  Title,
  Edit,
  Save,
  Cancel,
  Delete,
  Star
} from '@mui/icons-material';
import { achievementService } from '../../services/achievementService';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

const RARITY_COLORS = {
  common: '#9E9E9E',
  uncommon: '#4CAF50', 
  rare: '#2196F3',
  epic: '#9C27B0',
  legendary: '#FFD700'
};

const AchievementSettings = ({ open, onClose, user, onUpdate }) => {
  //console.log('AchievementSettings render:', { open, user: user?.id });
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [badges, setBadges] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [titles, setTitles] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({
    showBadges: true,
    maxDisplay: 3,
    visibleBadges: [],
    badgeOrder: [],
    achievementOrder: [],
    titleOrder: []
  });
  const [loading, setLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [customTitleName, setCustomTitleName] = useState('');

  useEffect(() => {
    //console.log('AchievementSettings useEffect:', { open, user: user?.id });
    if (open && user?.id) {
      //console.log('Loading data for user:', user.id);
      loadUserBadges();
      loadUserAchievements();
      loadUserTitles();
    }
  }, [open, user]);

  useEffect(() => {
    if (badges.length > 0) {
      loadDisplaySettings();
    }
  }, [badges, achievements]);

  const loadUserBadges = async () => {
    try {
      if (!user?.id) return;
      //console.log('Loading badges for user:', user.id);
      const userBadges = await achievementService.getUserBadges(user.id);
      //console.log('Loaded badges count:', userBadges.length);
      //console.log('Badge sample structure:', userBadges[0]);
      //console.log('Badge types:', userBadges.map(b => b.badgeType));
      //console.log('Badge names:', userBadges.map(b => b.name));
      setBadges(userBadges);
    } catch (error) {
      console.error('Error loading badges:', error);
    }
  };

  const loadUserAchievements = async () => {
    try {
      if (!user?.id) return;
      //console.log('Loading achievements for user:', user.id);
      const userAchievements = await achievementService.getUserAchievements(user.id);
      //console.log('Loaded achievements:', userAchievements);
      setAchievements(userAchievements);
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  const loadUserTitles = async () => {
    try {
      const userTitles = user?.titles || [];
      const { adminAchievementService } = await import('../../services/adminAchievementService');
      const titleDefs = await adminAchievementService.getAllTitles();
      
      setTitles(userTitles.map(titleType => {
        const titleDef = titleDefs.find(t => t.id === titleType || t.type === titleType);
        return {
          type: titleType,
          name: titleDef?.name || titleType,
          displayName: titleDef?.name || titleType,
          isCustom: false
        };
      }));
    } catch (error) {
      console.error('Error loading user titles:', error);
    }
  };

  const loadDisplaySettings = () => {
    try {
      //console.log('Loading display settings for user:', user);
      //console.log('Current badges for settings:', badges);
      
      const settings = user?.badgeDisplaySettings 
        ? JSON.parse(user.badgeDisplaySettings)
        : { 
            showBadges: true, 
            maxDisplay: 3, 
            visibleBadges: [],
            badgeOrder: [],
            achievementOrder: [],
            titleOrder: user?.titleOrder || []
          };
      
      // Initialize badge order with current badges
      if (badges.length > 0 && settings.badgeOrder.length === 0) {
        settings.badgeOrder = badges.map(b => b.badgeType || b.type || b.id);
      }
      
      // console.log('Final display settings:', settings);
      setDisplaySettings(settings);
    } catch (error) {
      console.error('Error loading display settings:', error);
      const defaultSettings = { 
        showBadges: true, 
        maxDisplay: 3, 
        visibleBadges: badges.map(b => b.badgeType || b.type || b.id),
        badgeOrder: badges.map(b => b.badgeType || b.type || b.id),
        achievementOrder: achievements.map(a => a.id),
        titleOrder: user?.titleOrder || []
      };
      setDisplaySettings(defaultSettings);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const UPDATE_USER = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            badgeDisplaySettings
            activeBadges
            titleOrder
            titles
          }
        }
      `;

      // Get active badges based on order and max display
      const activeBadges = badges
        .slice(0, displaySettings.maxDisplay)
        .map(b => b.badgeType || b.type || b.id);

      const result = await client.graphql({
        query: UPDATE_USER,
        variables: {
          input: {
            id: user.id,
            badgeDisplaySettings: JSON.stringify(displaySettings),
            activeBadges: activeBadges,
            titleOrder: displaySettings.titleOrder,
            titles: titles.map(t => t.type)
          }
        }
      });

      onUpdate?.(result.data.updateUser);
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };



  const handleDragStart = (e, item, type) => {
    setDraggedItem({ item, type });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex, type) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== type) return;

    const items = type === 'badge' ? badges : type === 'achievement' ? achievements : titles;
    const draggedIndex = items.findIndex(item => 
      type === 'badge' ? item.id === draggedItem.item.id :
      type === 'achievement' ? item.id === draggedItem.item.id :
      item.type === draggedItem.item.type
    );

    if (draggedIndex === targetIndex) return;

    const newItems = [...items];
    const [draggedItemData] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItemData);

    if (type === 'badge') {
      setBadges(newItems);
      setDisplaySettings(prev => ({
        ...prev,
        badgeOrder: newItems.map(b => b.badgeType || b.type || b.id)
      }));
    } else if (type === 'achievement') {
      setAchievements(newItems);
      setDisplaySettings(prev => ({
        ...prev,
        achievementOrder: newItems.map(a => a.id)
      }));
    } else {
      setTitles(newItems);
      setDisplaySettings(prev => ({
        ...prev,
        titleOrder: newItems.map(t => t.type)
      }));
    }

    setDraggedItem(null);
  };

  const handleEditTitle = (title) => {
    setEditingTitle(title.type);
    setCustomTitleName(title.displayName || title.name);
  };

  const handleSaveTitle = () => {
    setTitles(prev => prev.map(title => 
      title.type === editingTitle 
        ? { ...title, displayName: customTitleName, isCustom: true }
        : title
    ));
    setEditingTitle(null);
    setCustomTitleName('');
  };

  const handleCancelEdit = () => {
    setEditingTitle(null);
    setCustomTitleName('');
  };

  const handleDeleteTitle = (titleType) => {
    setTitles(prev => prev.filter(t => t.type !== titleType));
    setDisplaySettings(prev => ({
      ...prev,
      titleOrder: prev.titleOrder.filter(t => t !== titleType)
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          background: theme.palette.mode === 'dark'
            ? 'rgba(18, 18, 18, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 193, 7, 0.3)'
            : '1px solid rgba(25, 118, 210, 0.3)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(255, 193, 7, 0.2)'
            : '0 8px 32px rgba(25, 118, 210, 0.2)'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(0, 0, 0, 0.1))'
          : 'linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(255, 255, 255, 0.1))',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        🏆 Achievement & Profile Settings
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, rgba(255, 193, 7, 0.8), rgba(255, 193, 7, 1))'
                : 'linear-gradient(90deg, rgba(25, 118, 210, 0.8), rgba(25, 118, 210, 1))',
            }
          }}
        >
          <Tab icon={<Star />} label="Badges" />
          <Tab icon={<EmojiEvents />} label="Achievements" />
          <Tab icon={<Title />} label="Titles" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                💡 <strong>Tip:</strong> Drag badges to reorder them. The first {displaySettings.maxDisplay} badges will appear on your profile avatar!
              </Alert>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={displaySettings.showBadges}
                    onChange={(e) => setDisplaySettings(prev => ({ ...prev, showBadges: e.target.checked }))}
                  />
                }
                label="Show badges on profile"
                sx={{ mb: 2 }}
              />

              {displaySettings.showBadges && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Max badges to display: {displaySettings.maxDisplay}
                    </Typography>
                    <Slider
                      value={displaySettings.maxDisplay}
                      onChange={(e, value) => setDisplaySettings(prev => ({ ...prev, maxDisplay: value }))}
                      min={1}
                      max={10}
                      marks
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  {badges.length === 0 ? (
                    <Alert severity="info">
                      No badges earned yet. Complete achievements to earn badges!
                    </Alert>
                  ) : (
                    <List>
                      {badges.map((badge, index) => {
                        const isActive = index < displaySettings.maxDisplay;
                        return (
                          <ListItem 
                            key={badge.id || index}
                            draggable
                            onDragStart={(e) => handleDragStart(e, badge, 'badge')}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index, 'badge')}
                            sx={{ 
                              border: '1px solid', 
                              borderColor: isActive ? 'primary.main' : 'divider', 
                              borderRadius: 2, 
                              mb: 1,
                              background: isActive 
                                ? (theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)')
                                : (theme.palette.mode === 'dark' ? 'rgba(158, 158, 158, 0.05)' : 'rgba(158, 158, 158, 0.03)'),
                              cursor: 'grab',
                              opacity: isActive ? 1 : 0.6,
                              '&:active': { cursor: 'grabbing' }
                            }}
                          >
                            <IconButton size="small" sx={{ mr: 1 }}>
                              <DragIndicator />
                            </IconButton>
                            <Avatar 
                              sx={{ 
                                bgcolor: badge.color || RARITY_COLORS[badge.rarity] || 'primary.main',
                                width: 32,
                                height: 32,
                                mr: 2
                              }}
                            >
                              <Star />
                            </Avatar>
                            <ListItemText
                              primary={badge.name || badge.badgeType || 'Unknown Badge'}
                              secondary={`${badge.rarity || 'common'} • ${badge.points || 0} points • Earned ${badge.earnedAt ? new Date(badge.earnedAt).toLocaleDateString() : 'Unknown'}`}
                            />
                            <Chip 
                              label={isActive ? 'Active' : 'Inactive'} 
                              size="small" 
                              color={isActive ? 'primary' : 'default'}
                              sx={{ mr: 1 }}
                            />
                            <Chip 
                              label={badge.rarity || 'common'} 
                              size="small" 
                              sx={{ 
                                bgcolor: RARITY_COLORS[badge.rarity] || RARITY_COLORS.common,
                                color: 'white'
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Reorder how your achievements are displayed on your profile.
              </Alert>
              
              {achievements.length === 0 ? (
                <Alert severity="info">
                  No achievements found. Start interacting with content to unlock achievements!
                </Alert>
              ) : (
                <List>
                  {achievements.map((achievement, index) => (
                    <ListItem 
                      key={achievement.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, achievement, 'achievement')}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index, 'achievement')}
                      sx={{ 
                        border: '1px solid', 
                        borderColor: achievement.unlocked ? 'success.main' : 'divider', 
                        borderRadius: 2, 
                        mb: 1,
                        background: achievement.unlocked 
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)')
                          : (theme.palette.mode === 'dark' ? 'rgba(158, 158, 158, 0.05)' : 'rgba(158, 158, 158, 0.03)'),
                        cursor: 'grab',
                        opacity: achievement.unlocked ? 1 : 0.6,
                        '&:active': { cursor: 'grabbing' }
                      }}
                    >
                      <IconButton size="small" sx={{ mr: 1 }}>
                        <DragIndicator />
                      </IconButton>
                      <EmojiEvents sx={{ 
                        mr: 2, 
                        color: achievement.unlocked 
                          ? (theme.palette.mode === 'dark' ? '#FFD700' : '#FFA000')
                          : 'grey.500' 
                      }} />
                      <ListItemText
                        primary={achievement.achievementType}
                        secondary={`Progress: ${achievement.progress || 0}% ${achievement.unlocked ? '(Unlocked)' : '(Locked)'}`}
                      />
                      <Chip 
                        label={achievement.unlocked ? 'Unlocked' : 'Locked'} 
                        size="small" 
                        color={achievement.unlocked ? 'success' : 'default'}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}

          {activeTab === 2 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Customize your title display names and reorder them. The first title will be your primary display title.
              </Alert>
              
              {titles.length === 0 ? (
                <Alert severity="info">
                  No titles available. Earn achievements to unlock titles!
                </Alert>
              ) : (
                <List>
                  {titles.map((title, index) => (
                    <ListItem 
                      key={title.type}
                      draggable
                      onDragStart={(e) => handleDragStart(e, title, 'title')}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index, 'title')}
                      sx={{ 
                        border: '1px solid', 
                        borderColor: index === 0 ? 'primary.main' : 'divider', 
                        borderRadius: 2, 
                        mb: 1,
                        background: index === 0 
                          ? (theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(25, 118, 210, 0.1)')
                          : (theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.05)' : 'rgba(25, 118, 210, 0.05)'),
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' }
                      }}
                    >
                      <IconButton size="small" sx={{ mr: 1 }}>
                        <DragIndicator />
                      </IconButton>
                      <Title sx={{ mr: 2, color: index === 0 ? 'primary.main' : 'text.secondary' }} />
                      
                      {editingTitle === title.type ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            value={customTitleName}
                            onChange={(e) => setCustomTitleName(e.target.value)}
                            size="small"
                            placeholder="Enter custom title name"
                            sx={{ flex: 1 }}
                          />
                          <IconButton size="small" onClick={handleSaveTitle} color="primary">
                            <Save />
                          </IconButton>
                          <IconButton size="small" onClick={handleCancelEdit}>
                            <Cancel />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <ListItemText
                            primary={title.displayName || title.name}
                            secondary={`${title.type}${index === 0 ? ' (Primary)' : ''}${title.isCustom ? ' (Custom)' : ''}`}
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton onClick={() => handleEditTitle(title)} size="small">
                                <Edit />
                              </IconButton>
                              {titles.length > 1 && (
                                <IconButton 
                                  onClick={() => handleDeleteTitle(title.type)} 
                                  size="small"
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              )}
                            </Box>
                          </ListItemSecondaryAction>
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.8), rgba(255, 193, 7, 1))'
              : 'linear-gradient(135deg, rgba(25, 118, 210, 0.8), rgba(25, 118, 210, 1))'
          }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AchievementSettings;